// A customer's email in a sandboxed frame: it keeps its own design and cannot reach the dashboard.
import { useEffect, useMemo, useRef, useState } from 'react';
import { emailFrameDocument, QUOTE_HIDDEN_CLASS, sanitizeEmailHtml } from './email-html';

interface EmailHtmlContentProps {
  /** The stored email HTML, its quoted history marked by `markQuotedTail`. Renders nothing when empty. */
  html: string;
  /** Shows the quoted history; hidden by default, as a mail client folds it. */
  quotedVisible?: boolean;
}

export function EmailHtmlContent({ html, quotedVisible = false }: EmailHtmlContentProps): React.JSX.Element | null {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(0);
  const frameDocument = useMemo(() => {
    const safeHtml = html.trim() ? sanitizeEmailHtml(html) : '';
    return safeHtml ? emailFrameDocument(safeHtml, getComputedStyle(document.body).fontFamily) : '';
  }, [html]);

  // Written straight into the frame, not loaded through srcdoc: the email is laid out and measured at once, instead of
  // after a load event that waits for every image in it. Grows with the email, so the bubble shows all of it and the
  // frame never scrolls.
  useEffect(() => {
    const page = frame.current?.contentDocument;
    if (!page || !frameDocument) return;
    page.open();
    page.write(frameDocument);
    page.close();
    // The root element's own box: it holds the body and every margin that collapses out of it, and unlike scrollHeight
    // it is not clamped to the frame, so a folded quote shrinks the frame back.
    const fit = (): void => setHeight(page.documentElement.offsetHeight);
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(fit);
    observer.observe(page.body);
    return () => observer.disconnect();
  }, [frameDocument]);

  // The quote folds and unfolds inside the written document, so nothing reloads; the body observer then refits the height.
  useEffect(() => {
    frame.current?.contentDocument?.documentElement.classList.toggle(QUOTE_HIDDEN_CLASS, !quotedVisible);
  }, [frameDocument, quotedVisible]);

  if (!frameDocument) return null;
  return (
    <iframe
      ref={frame}
      title="Customer email"
      // No allow-scripts: nothing in the email runs; same origin only lets us read its height.
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      style={{ height }}
      className="block w-full border-0"
    />
  );
}
